import { useState, KeyboardEvent, MouseEvent, HTMLAttributes, ReactNode } from 'react';
import cx from 'classnames';

import s from './styles.module.scss';

interface SummaryProps extends HTMLAttributes<HTMLDivElement> {
  summaryClassName?: string;
}

interface ContentProps extends HTMLAttributes<HTMLDivElement> {
  contentWrapperClassName?: string;
  contentClassName?: string;
  contentId?: string;
}

export interface AccordionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  summary?: ReactNode;
  content?: ReactNode;
  summaryProps?: SummaryProps;
  contentProps?: ContentProps;
  className?: string;
  open?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
}

export const Accordion: React.FC<AccordionProps> = ({
  summary = null,
  content = null,
  summaryProps = {},
  contentProps = {
    contentWrapperClassName: '',
    contentClassName: '',
    contentId: '',
  },
  className = '',
  open = false,
  onClick = null,
  ...props
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const toggleAcc = (e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
    if (onClick) {
      return onClick(e);
    }
    setInternalOpen(!internalOpen);
    return true;
  };

  const isOpen = internalOpen || open;

  return (
    <div className={cx(s.accordion_item, isOpen && s.accordion_item__opened, className)} {...props}>
      <div
        className={cx(s.accordion_item__line, summaryProps?.summaryClassName)}
        onClick={toggleAcc}
        onKeyDown={toggleAcc}
        role="button"
        tabIndex={0}
        {...summaryProps}
      >
        <span>{summary}</span>
        <span className={s.accordion_item__icon}>
          {/* simple chevron svg */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
      {content && (
        <div className={cx(s.accordion_item__inner, contentProps?.contentWrapperClassName)}>
          <div
            className={cx(s.accordion_item__content, contentProps?.contentClassName)}
            id={contentProps?.contentId}
            {...contentProps}
          >
            {content}
          </div>
        </div>
      )}
    </div>
  );
};
